import React, { useState } from 'react';
import { useConfigureHubCommunity } from '../hooks/useConfigureHubCommunity';

interface INetworkedHubCardProps {
	communityId: string;
	isNetworkedHub: boolean;
	onRefetch: () => void;
}

const NetworkedHubCard: React.FC<INetworkedHubCardProps> = ({
	communityId,
	isNetworkedHub,
	onRefetch,
}) => {
	const { loading, error, result, configureHubCommunity } = useConfigureHubCommunity();
	const [confirming, setConfirming] = useState(false);

	const isConverted = isNetworkedHub || result?.isNetworkedHub === true;

	const handleConvert = async () => {
		const res = await configureHubCommunity(communityId);
		if (res) {
			setConfirming(false);
			onRefetch();
		}
	};

	return (
		<div className="card card-info mb-3">
			<div className="card-header">
				<h3 className="card-title">
					<i className="fas fa-sitemap mr-2"></i>
					Networked Hub
				</h3>
				<div className="card-tools">
					<button type="button" className="btn btn-tool" data-card-widget="collapse" title="Collapse">
						<i className="fas fa-minus"></i>
					</button>
				</div>
			</div>
			<div className="card-body">
				<div className="d-flex align-items-start">
					<div className="np-os-icon-wrap mr-3">
						<i className={`fas fa-sitemap fa-lg ${isConverted ? 'text-success' : 'text-primary'}`}></i>
					</div>
					<div className="flex-grow-1">
						<h6 className="mb-2 font-weight-bold">Convert this community into a Networked Hub community</h6>
						<p className="text-muted mb-3">
							A Networked Hub acts as the parent community for related communities owned or administered by the same person.
						</p>

						<ul className="list-group list-group-flush mb-3">
							<li className="list-group-item px-0 py-2" style={{ background: 'transparent' }}>
								<i className="fas fa-angle-right text-muted mr-2"></i>
								Communities created by this community's owner or admin become hub communities under this parent community.
							</li>
							<li className="list-group-item px-0 py-2" style={{ background: 'transparent' }}>
								<i className="fas fa-angle-right text-muted mr-2"></i>
								Those hub communities use the same link behavior considered by the parent community.
							</li>
							<li className="list-group-item px-0 py-2" style={{ background: 'transparent' }}>
								<i className="fas fa-angle-right text-muted mr-2"></i>
								The three-button settings are carried forward from the parent community to the hub communities.
							</li>
						</ul>

						{isConverted && (
							<div className="alert alert-success py-2 mb-0">
								<i className="fas fa-check-circle mr-2"></i>
								<strong>This community is already a Networked Hub community.</strong>
								{result?.hubAdminId && (
									<div className="small mt-1">
										Hub Admin ID: <code>{result.hubAdminId}</code>
									</div>
								)}
							</div>
						)}

						{error && (
							<div className="alert alert-danger py-2 mb-3">
								<i className="fas fa-exclamation-circle mr-1"></i>
								{error}
							</div>
						)}

						{!isConverted && !confirming && (
							<button
								type="button"
								className="np-os-btn btn btn-primary btn-lg px-5"
								onClick={() => setConfirming(true)}
								disabled={loading}
							>
								<i className="fas fa-exchange-alt mr-2"></i>
								Convert to Networked Hub Community
							</button>
						)}

						{!isConverted && confirming && (
							<div className="alert alert-warning mb-0">
								<div className="mb-3">
									<i className="fas fa-exclamation-triangle mr-2"></i>
									<strong>Confirm Networked Hub conversion.</strong>
									<div className="small mt-1">
										This will update related communities for the same owner or admin. Continue only if this parent community is correct.
									</div>
								</div>
								<button
									type="button"
									className="btn btn-warning mr-2"
									onClick={handleConvert}
									disabled={loading}
								>
									{loading ? (
										<>
											<span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
											Converting...
										</>
									) : (
										<>
											<i className="fas fa-check mr-2"></i>
											Yes, Convert Community
										</>
									)}
								</button>
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => setConfirming(false)}
									disabled={loading}
								>
									Cancel
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default NetworkedHubCard;
